import { Button, Card, Container, Grid, Input, InputAdornment, Modal, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldIcon } from '/@/components/field-info-v2/field-card';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2, convertAreaToM2 } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';

const FieldNameModal = ({ showName, field, handleCancelName, handleSubmit, loading }) => {
    const units = useUnitProvider();
    const [name, setName] = useState('');
    const [area, setArea] = useState<number | undefined>(convertAreaFromM2(field?.area || 0, units).value);

    const areaToUpload = convertAreaToM2(area || 0, units).value;

    useEffect(() => {
        if (field?.area) {
            setArea(convertAreaFromM2(field?.area || 0, units).value);
        } else {
            setArea(undefined);
        }
    }, [field?.area]);

    return (
        <Modal open={showName}>
            <Container
                maxWidth='xs'
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            >
                <Card
                    sx={{
                        border: (t) => `1px solid ${t.palette.divider}`,
                        borderRadius: '4px',
                    }}
                >
                    <Stack
                        sx={{
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                        alignItems={'center'}
                        p={2}
                    >
                        <Typography fontWeight={500} fontSize={'20px'}>
                            {_('Ingresar nombre del lote')}
                        </Typography>
                    </Stack>

                    {loading ? (
                        <Stack m={2} spacing={2} justifyContent={'center'} alignItems={'center'} height={90}>
                            <Spinner />
                        </Stack>
                    ) : (
                        <Stack m={2} spacing={2}>
                            <Input
                                placeholder={_('Nombre')}
                                onChange={(e) => setName(e.target.value)}
                                onKeyUp={(event) => {
                                    if (event.key === 'Enter' && area) handleSubmit(name, areaToUpload);
                                    if (event.key === 'Escape') handleCancelName();
                                }}
                                data-testid='name-modal-input-name'
                            />
                            {!field.area && (
                                <Input
                                    placeholder={_('Superficie')}
                                    type='number'
                                    onChange={(e) => setArea(units.toArea(Number(e.target.value), 2).value)}
                                    endAdornment={
                                        <InputAdornment position='start'>
                                            {units.getUnit('area', 'corn')}
                                        </InputAdornment>
                                    }
                                    onKeyUp={(event) => {
                                        if (event.key === 'Enter') handleSubmit(name, areaToUpload);
                                        if (event.key === 'Escape') handleCancelName();
                                    }}
                                />
                            )}
                            <Grid container alignItems={'center'}>
                                <Grid item xs={2}>
                                    <FieldIcon coordinates={field.geometry} selected />
                                </Grid>
                                {area && <Grid item>{units.toArea(area, 2).toString()}</Grid>}
                            </Grid>
                        </Stack>
                    )}

                    <Stack
                        direction={'row'}
                        justifyContent={'flex-end'}
                        sx={{
                            borderTop: (t) => `1px solid ${t.palette.divider}`,
                        }}
                        alignItems={'center'}
                        p={2}
                        spacing={2}
                    >
                        <Button
                            color='secondary'
                            variant='contained'
                            sx={{ background: 'white', color: (t) => t.palette.secondary.main }}
                            onClick={() => handleCancelName()}
                        >
                            {_('Cancelar')}
                        </Button>
                        <Button
                            color='secondary'
                            variant='contained'
                            onClick={() => handleSubmit(name, areaToUpload)}
                            disabled={name.length === 0}
                            data-testid='name-modal-button-save'
                        >
                            {_('Guardar')}
                        </Button>
                    </Stack>
                </Card>
            </Container>
        </Modal>
    );
};

export default FieldNameModal;
